import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars } from '@fortawesome/free-solid-svg-icons';
import Logo from './Logo';
import Button from './Button';

const NavItem = ({ to, children, isActive, hasDropdown }) => {
  return (
    <div className={hasDropdown ? "relative group" : ""}>
      <Link 
        to={to} 
        className={`${isActive ? 'text-primary font-semibold' : 'text-gray-700 hover:text-primary'} flex items-center`}
      >
        {children}
      </Link>
    </div>
  );
};

const Header = ({ activePage = 'home' }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Handle click outside to close mobile menu
  useEffect(() => {
    const handleClickOutside = (e) => {
      const mobileMenu = document.getElementById('mobile-menu');
      const mobileMenuButton = document.getElementById('mobile-menu-button');
      
      if (mobileMenu && !mobileMenu.contains(e.target) && !mobileMenuButton.contains(e.target)) {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  const toggleMobileMenu = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const headerClasses = scrolled 
    ? 'bg-white/95 backdrop-blur-sm shadow-lg' 
    : 'bg-white';

  return (
    <header className={`${headerClasses} fixed top-0 left-0 right-0 z-50 transition-all duration-300`}>
      <div className="container mx-auto px-4 py-3">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <Link to="/">
            <Logo />
          </Link>
          
          {/* Navigation */}
          <nav className="hidden md:flex items-center space-x-6">
            <NavItem to="/" isActive={activePage === 'home'}>
              Beranda
            </NavItem>
            <NavItem to="/tentang" isActive={activePage === 'about'}>
              Tentang
            </NavItem>
            <NavItem to="/program" isActive={activePage === 'program'} hasDropdown>
              Program & Kegiatan
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
              </svg>
            </NavItem>
            <NavItem to="/cabang" isActive={activePage === 'branch'} hasDropdown>
              Cabang
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
              </svg>
            </NavItem>
            <NavItem to="/media" isActive={activePage === 'media'} hasDropdown>
              Media
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
              </svg>
            </NavItem>
            <NavItem to="/afiliasi" isActive={activePage === 'affiliate'}>
              Afiliasi
            </NavItem>
            <NavItem to="/kontak" isActive={activePage === 'contact'}>
              Hubungi Kami
            </NavItem>
          </nav>
          
          {/* Login Button - Hidden on mobile */}
          <div className="hidden md:block">
            <Button to="/login">Login</Button>
          </div>
          
          {/* Mobile Menu Button */}
          <button 
            id="mobile-menu-button" 
            className="block md:hidden text-gray-700 focus:outline-none"
            onClick={toggleMobileMenu}
          >
            <FontAwesomeIcon icon={faBars} className="h-6 w-6" />
          </button>
        </div>
      </div>
      
      {/* Mobile Menu */}
      <div 
        id="mobile-menu" 
        className={`md:hidden bg-white py-4 px-4 shadow-lg absolute w-full top-16 left-0 z-50 mobile-menu ${mobileMenuOpen ? '' : 'hidden'}`}
      >
        <nav className="flex flex-col space-y-3">
          <Link to="/" className={`${activePage === 'home' ? 'text-primary font-semibold' : 'text-gray-700 hover:text-primary'} py-2`} onClick={closeMobileMenu}>
            Beranda
          </Link>
          <Link to="/tentang" className={`${activePage === 'about' ? 'text-primary font-semibold' : 'text-gray-700 hover:text-primary'} py-2`} onClick={closeMobileMenu}>
            Tentang
          </Link>
          <Link to="/program" className={`${activePage === 'program' ? 'text-primary font-semibold' : 'text-gray-700 hover:text-primary'} py-2`} onClick={closeMobileMenu}>
            Program & Kegiatan
          </Link>
          <Link to="/cabang" className={`${activePage === 'branch' ? 'text-primary font-semibold' : 'text-gray-700 hover:text-primary'} py-2`} onClick={closeMobileMenu}>
            Cabang
          </Link>
          <Link to="/media" className={`${activePage === 'media' ? 'text-primary font-semibold' : 'text-gray-700 hover:text-primary'} py-2`} onClick={closeMobileMenu}>
            Media
          </Link>
          <Link to="/afiliasi" className={`${activePage === 'affiliate' ? 'text-primary font-semibold' : 'text-gray-700 hover:text-primary'} py-2`} onClick={closeMobileMenu}>
            Afiliasi
          </Link>
          <Link to="/kontak" className={`${activePage === 'contact' ? 'text-primary font-semibold' : 'text-gray-700 hover:text-primary'} py-2`} onClick={closeMobileMenu}>
            Hubungi Kami
          </Link>
          
          {/* Mobile Login and Register buttons */}
          <div className="border-t border-gray-200 pt-3 mt-3">
            <Link 
              to="/login" 
              className="block w-full text-center py-2 px-4 mb-2 border border-primary text-primary rounded-lg hover:bg-primary hover:text-white transition-colors duration-200"
              onClick={closeMobileMenu}
            >
              Login
            </Link>
            <Button to="/daftar" className="block w-full text-center" onClick={closeMobileMenu}>Daftar Sekarang</Button>
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Header;
